import React, { useEffect, useMemo, useState } from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminDashboard.css";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const integerFormatter = new Intl.NumberFormat("pt-BR");

const getArrayFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentDateKey = () => formatDateKey(new Date());

const getMonthPrefix = (dateKey) => (dateKey ? dateKey.slice(0, 7) : "");

const parseRawJson = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
};

const formatPlatformName = (value) => {
  const clean = String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) return "Nao informado";

  return clean
    .toLowerCase()
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getOrderPlatform = (order) => {
  const raw = parseRawJson(order?.raw_json);

  return formatPlatformName(
    order?.origem_pedido ||
      raw?.cabecalho?.origem_pedido ||
      raw?.Cabecalho?.origem_pedido ||
      raw?.origem_pedido ||
      raw?.canal_venda
  );
};

const getOrderValue = (order) =>
  toNumber(
    order?.total_value ??
      order?.valor_total_pedido ??
      order?.purchase_amount ??
      order?.valor_compra
  );

const isConfirmedCommission = (entry) => {
  const entryType = String(entry?.entry_type || "").toUpperCase();
  const status = String(entry?.status || "").toUpperCase();
  const origin = String(entry?.origin || "").toUpperCase();

  if (entryType !== "CREDIT") return false;
  if (status && status !== "CONFIRMED") return false;
  if (origin && origin !== "COMMISSION") return false;
  return true;
};

const getLinePath = (values, width, height, padding = 26) => {
  if (!values.length) return "";
  if (values.length === 1) {
    const y = height - padding;
    return `M ${padding} ${y} L ${width - padding} ${y}`;
  }

  const max = Math.max(...values, 1);
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;

  return values
    .map((value, index) => {
      const x = padding + (index * usableWidth) / (values.length - 1);
      const normalized = max === 0 ? 0 : value / max;
      const y = height - padding - normalized * usableHeight;
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");
};

const DashboardMetricCard = ({ tone, title, value, subtitle }) => (
  <article className={`admin-kpi-card ${tone}`}>
    <div className="admin-kpi-title">{title}</div>
    <div className="admin-kpi-value">{value}</div>
    <div className="admin-kpi-subtitle">{subtitle}</div>
  </article>
);

const DashboardPlatformList = ({ title, rows, type = "count" }) => {
  const maxValue = useMemo(() => {
    if (!rows.length) return 0;
    return Math.max(
      ...rows.map((row) => (type === "count" ? row.count : row.totalValue)),
      0
    );
  }, [rows, type]);

  return (
    <article className="admin-panel">
      <div className="admin-panel-header">
        <h3>{title}</h3>
      </div>

      <div className="admin-platform-list">
        {rows.length === 0 ? (
          <div className="admin-empty">Nenhum dado disponivel no periodo atual.</div>
        ) : (
          rows.map((row) => {
            const rawValue = type === "count" ? row.count : row.totalValue;
            const percent = maxValue > 0 ? (rawValue / maxValue) * 100 : 0;

            return (
              <div className="admin-platform-row" key={`${type}-${row.platform}`}>
                <div className="admin-platform-head">
                  <span className="admin-platform-name">{row.platform}</span>
                  <span className="admin-platform-value">
                    {type === "count"
                      ? `${integerFormatter.format(rawValue)} vendas`
                      : currencyFormatter.format(rawValue)}
                  </span>
                </div>
                <div className="admin-platform-bar">
                  <span style={{ width: `${percent}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </article>
  );
};

const AdminDashboard = () => {
  const [menuClose, setMenuClose] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [metrics, setMetrics] = useState({
    activeLicensed: 0,
    salesTodayValue: 0,
    salesTodayCount: 0,
    salesMonthValue: 0,
    salesMonthCount: 0,
    commissionTodayValue: 0,
    commissionMonthValue: 0,
    platforms: [],
    trend: [],
    updatedAt: null,
  });

  const toggleMenu = () => setMenuClose((prev) => !prev);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError("");

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const todayKey = getCurrentDateKey();
        const monthPrefix = getMonthPrefix(todayKey);

        const [usersRes, ordersRes, ledgerRes] = await Promise.allSettled([
          Api.get("/usuarios"),
          Api.get("/omie/orders", {
            params: {
              date_from: formatDateKey(monthStart),
              date_to: todayKey,
              limit: 500,
            },
          }),
          Api.get("/ledger-entries", { params: { limit: 1000 } }),
        ]);

        const users = usersRes.status === "fulfilled" ? getArrayFromResponse(usersRes.value?.data) : [];
        const orders =
          ordersRes.status === "fulfilled" ? getArrayFromResponse(ordersRes.value?.data) : [];
        const ledgerEntries =
          ledgerRes.status === "fulfilled" ? getArrayFromResponse(ledgerRes.value?.data) : [];

        const failedRequests = [
          usersRes.status === "rejected" ? "usuarios" : null,
          ordersRes.status === "rejected" ? "pedidos Omie" : null,
          ledgerRes.status === "rejected" ? "lancamentos" : null,
        ].filter(Boolean);

        if (failedRequests.length > 0) {
          console.error("Falhas parciais ao carregar dashboard:", {
            usersError: usersRes.status === "rejected" ? usersRes.reason : null,
            ordersError: ordersRes.status === "rejected" ? ordersRes.reason : null,
            ledgerError: ledgerRes.status === "rejected" ? ledgerRes.reason : null,
          });
          setError(
            `Alguns dados nao puderam ser carregados: ${failedRequests.join(", ")}.`
          );
        }

        const activeLicensed = users.filter(
          (user) =>
            String(user?.access_type || "").toUpperCase() === "LICENSED" &&
            String(user?.status || "").toUpperCase() === "ACTIVE"
        ).length;

        const monthOrders = orders.filter((order) => {
          const key = formatDateKey(order?.order_date);
          return key && key.startsWith(monthPrefix);
        });

        const salesByDay = new Map();
        const platformMap = new Map();
        let salesTodayValue = 0;
        let salesMonthValue = 0;
        let salesTodayCount = 0;

        monthOrders.forEach((order) => {
          const dayKey = formatDateKey(order?.order_date);
          if (!dayKey) return;

          const value = getOrderValue(order);
          salesMonthValue += value;

          if (dayKey === todayKey) {
            salesTodayValue += value;
            salesTodayCount += 1;
          }

          salesByDay.set(dayKey, (salesByDay.get(dayKey) || 0) + value);

          const platform = getOrderPlatform(order);
          const current = platformMap.get(platform) || {
            platform,
            count: 0,
            totalValue: 0,
          };
          current.count += 1;
          current.totalValue += value;
          platformMap.set(platform, current);
        });

        const commissionByDay = new Map();
        let commissionTodayValue = 0;
        let commissionMonthValue = 0;

        ledgerEntries
          .filter((entry) => isConfirmedCommission(entry))
          .forEach((entry) => {
            const dayKey = formatDateKey(entry?.entry_date);
            if (!dayKey || !dayKey.startsWith(monthPrefix)) return;

            const value = Math.max(0, toNumber(entry?.amount));
            commissionMonthValue += value;
            commissionByDay.set(dayKey, (commissionByDay.get(dayKey) || 0) + value);

            if (dayKey === todayKey) {
              commissionTodayValue += value;
            }
          });

        const platforms = Array.from(platformMap.values()).sort(
          (a, b) => b.totalValue - a.totalValue
        );

        const currentDay = now.getDate();
        const trend = [];
        for (let day = 1; day <= currentDay; day += 1) {
          const date = new Date(now.getFullYear(), now.getMonth(), day);
          const dayKey = formatDateKey(date);
          trend.push({
            day: String(day).padStart(2, "0"),
            salesValue: salesByDay.get(dayKey) || 0,
            commissionValue: commissionByDay.get(dayKey) || 0,
          });
        }

        setMetrics({
          activeLicensed,
          salesTodayValue,
          salesTodayCount,
          salesMonthValue,
          salesMonthCount: monthOrders.length,
          commissionTodayValue,
          commissionMonthValue,
          platforms,
          trend,
          updatedAt: new Date(),
        });
      } catch (err) {
        console.error("Erro ao carregar dashboard admin:", err);
        setError("Nao foi possivel carregar os dados da dashboard.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const chartWidth = 920;
  const chartHeight = 280;
  const salesPath = getLinePath(
    metrics.trend.map((point) => point.salesValue),
    chartWidth,
    chartHeight
  );
  const commissionPath = getLinePath(
    metrics.trend.map((point) => point.commissionValue),
    chartWidth,
    chartHeight
  );

  const lastUpdate = metrics.updatedAt
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(metrics.updatedAt)
    : "-";

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} dashboardActive="active" />

      <main className="main-content">
        <Topbar page="Dashboard Admin" onToggleMenu={toggleMenu} />

        {error ? <div className="admin-dashboard-alert">{error}</div> : null}

        <section className="admin-kpi-grid">
          <DashboardMetricCard
            tone="tone-orange"
            title="Licenciados ativos"
            value={integerFormatter.format(metrics.activeLicensed)}
            subtitle="Usuarios com status ACTIVE e perfil LICENSED"
          />
          <DashboardMetricCard
            tone="tone-emerald"
            title="Vendas de hoje"
            value={currencyFormatter.format(metrics.salesTodayValue)}
            subtitle={`${integerFormatter.format(metrics.salesTodayCount)} vendas no dia`}
          />
          <DashboardMetricCard
            tone="tone-blue"
            title="Vendas no mes"
            value={currencyFormatter.format(metrics.salesMonthValue)}
            subtitle={`${integerFormatter.format(metrics.salesMonthCount)} vendas acumuladas`}
          />
          <DashboardMetricCard
            tone="tone-pink"
            title="Comissao paga hoje"
            value={currencyFormatter.format(metrics.commissionTodayValue)}
            subtitle="Somente creditos confirmados"
          />
          <DashboardMetricCard
            tone="tone-slate"
            title="Comissao paga no mes"
            value={currencyFormatter.format(metrics.commissionMonthValue)}
            subtitle={`Atualizado em ${lastUpdate}`}
          />
        </section>

        <section className="admin-panel-grid">
          <article className="admin-panel admin-trend-panel">
            <div className="admin-panel-header">
              <h3>Tendencia diaria no mes atual</h3>
              <p>
                Vendas totais x comissao confirmada ({loading ? "carregando..." : "dados atualizados"})
              </p>
            </div>

            {metrics.trend.length === 0 ? (
              <div className="admin-empty">Sem dados para montar o grafico.</div>
            ) : (
              <>
                <div className="admin-chart-legend">
                  <span className="legend-item">
                    <i className="legend-dot sales" />
                    Vendas
                  </span>
                  <span className="legend-item">
                    <i className="legend-dot commission" />
                    Comissao
                  </span>
                </div>

                <div className="admin-chart-wrapper">
                  <svg
                    className="admin-trend-chart"
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    role="img"
                    aria-label="Grafico de tendencia de vendas e comissao"
                  >
                    {[0, 1, 2, 3].map((line) => {
                      const y = 26 + (line * (chartHeight - 52)) / 3;
                      return (
                        <line
                          key={`grid-${line}`}
                          x1="26"
                          x2={chartWidth - 26}
                          y1={y}
                          y2={y}
                          className="chart-grid-line"
                        />
                      );
                    })}

                    <path d={salesPath} className="chart-line sales" />
                    <path d={commissionPath} className="chart-line commission" />
                  </svg>
                </div>

                <div className="admin-chart-axis">
                  <span>{metrics.trend[0]?.day || "--"}</span>
                  <span>{metrics.trend[Math.floor(metrics.trend.length / 2)]?.day || "--"}</span>
                  <span>{metrics.trend[metrics.trend.length - 1]?.day || "--"}</span>
                </div>
              </>
            )}
          </article>

          <article className="admin-panel admin-highlight-panel">
            <div className="admin-panel-header">
              <h3>Resumo rapido</h3>
            </div>

            <div className="admin-highlight-list">
              <div className="admin-highlight-item">
                <span>Ticket medio no mes</span>
                <strong>
                  {currencyFormatter.format(
                    metrics.salesMonthCount > 0
                      ? metrics.salesMonthValue / metrics.salesMonthCount
                      : 0
                  )}
                </strong>
              </div>
              <div className="admin-highlight-item">
                <span>Comissao / Vendas no mes</span>
                <strong>
                  {metrics.salesMonthValue > 0
                    ? `${((metrics.commissionMonthValue / metrics.salesMonthValue) * 100).toFixed(
                        2
                      )}%`
                    : "0,00%"}
                </strong>
              </div>
              <div className="admin-highlight-item">
                <span>Plataformas com venda no mes</span>
                <strong>{integerFormatter.format(metrics.platforms.length)}</strong>
              </div>
            </div>
          </article>
        </section>

        <section className="admin-compare-grid">
          <DashboardPlatformList
            title="Numero de vendas por plataforma (mes atual)"
            rows={metrics.platforms}
            type="count"
          />

          <DashboardPlatformList
            title="Valor de vendas por plataforma (mes atual)"
            rows={metrics.platforms}
            type="value"
          />
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
